import {flow, getEnv, getRoot, resolveIdentifier, types} from "mobx-state-tree";
import PreviewManager from "../../../managers/preview/PreviewManager";
import {QrConfigSelectState} from "../../elements/qrconfig/QrConfigSelectState";
import QrConfiguration from "../../../models/QrConfiguration";
import {ModalState} from "../../elements/modal/GenericModal";
import {saveAs} from "file-saver";
import {isEpsFormat, setDefaultBackgroundColor, setEyesAndModules, getCacheBustedUrl} from "../../../utils/common";
import {scannabilityCheck} from "../../../utils/scannability";
import {QrDownloadState} from "./qrDownload/QrDownloadState";
import { DashboardStore } from "src/stores/domain/DashboardStore";
import Notification from "../../../utils/Notification";
import { formatFileName } from "../../../utils/common";
import { protocolConstants, DEFAULT_QR_PLACEHOLDER } from "../../../utils/constants";

const QrPreviewState = types.model('QrPreviewState', {
  title:types.optional(types.string, ""),
  isLoading: types.optional(types.boolean, false),
  testMode: types.optional(types.boolean, false),
  preSaveMode: types.optional(types.boolean, true),
  previewImage: types.optional(types.string, ""),
  imageFormat: types.optional(types.string, "png"),
  shortUrl: types.optional(types.string, DEFAULT_QR_PLACEHOLDER),
  qrProtocol: types.optional(types.enumeration([protocolConstants.HTTP, protocolConstants.HTTPS]), protocolConstants.HTTP),
  originalProtocol: types.optional(types.enumeration([protocolConstants.HTTP, protocolConstants.HTTPS]), protocolConstants.HTTP),
  experienceData: types.optional(types.frozen(),{}),
  qrType: types.maybe(types.string),
  qrConfigSelectStore: types.maybe(types.late(() => types.reference(QrConfigSelectState))),
  qrConfigSelected: types.maybe(types.reference(QrConfiguration)),
  baseQRImageFormData: types.optional(types.string, ''),
  qrImageFormData: types.optional(types.string, ''),
  modalStore: types.maybe(ModalState),
  ableToEditConfig: types.optional(types.boolean, true),
  epsPdfDownloadFile: types.optional(types.string, ''),
  isEditCodePage: types.optional(types.boolean, false),
  validQr: types.optional(types.boolean, true),
  qrDownloadStore: types.optional(QrDownloadState, () => QrDownloadState.create({})),
  // dashboardStore: types.maybe(types.late(() => DashboardStore)),
  defaultImageConfig: types.optional(types.frozen(), {}),
  v1ImageConfig: types.optional(types.frozen(), {}),
  base64NonBrowserConvertedFile: types.optional(types.string, ''),
  moduleWidth: types.optional(types.string, "0"),
  qrWidth: types.optional(types.string, "0"),
  qrWidthPixel: types.optional(types.string, "0"),
  qrWidthInch: types.optional(types.string, "0"),
  qrWidthMM: types.optional(types.string, "0"),
  qrVersion: types.optional(types.number, 0),
  modulePerSide: types.optional(types.number, 0),
  errorCorrection: types.optional(types.string, ''),
  unit: types.optional(types.string, ''),
  quietZone: types.optional(types.number, 0),
  moduleWidthInPixel: types.optional(types.string, "0"),
  moduleWidthInInch: types.optional(types.string, "0"),
  moduleWidthInMM: types.optional(types.string, "0"),
  showDownloadButton: types.optional(types.boolean, true),
  bwr: types.maybeNull(types.string),
  isStatic: types.optional(types.boolean, false),
})
  .volatile((self) => ({
    modalComponent: types.object,
    modalComponentStore: types.object,
    toastComponentStore: types.object,
  }))
  .actions((self) => ({
    replaceProtocol(url, newProtocol) {
      if (!url) return url;
      if (self.isStatic) return url;
      // If URL has a protocol, replace it; otherwise prepend the protocol
      if (/^https?:\/\//i.test(url)) {
        return url.replace(/^https?:\/\//i, newProtocol);
      }
      return newProtocol + url;
    },
    setIsStatic(value) {
      self.isStatic = value;
    },
    updateImage(shortUrl) {
      console.log("QrPreviewState updateImage:", shortUrl)
      self.shortUrl = self.replaceProtocol(shortUrl, self.qrProtocol)
      self.setQRImageFormData(self.qrImageFormData ? self.qrImageFormData : self.baseQRImageFormData)
    },
    setShortUrl(shortUrl) {
      self.shortUrl = self.replaceProtocol(shortUrl, self.qrProtocol)
    },
    isValidShortUrl() {
      return !!self.shortUrl && self.shortUrl !== DEFAULT_QR_PLACEHOLDER;
    },
    setQrProtocol(protocol) {
      self.qrProtocol = protocol;
      if (self.isValidShortUrl()) {
        self.shortUrl = self.replaceProtocol(self.shortUrl, protocol);
        self.generateImage();
      }
    },
    setOriginalProtocol(protocol) {
      self.originalProtocol = protocol;
      self.qrProtocol = protocol;
      // shortUrl may have been set via setShortUrl()/the urlListener during
      // fetchWithID() before the real protocol was known (qrProtocol still
      // defaulted to HTTP). Re-apply the now-correct protocol so the preview
      // doesn't display a stale http:// when the code is actually https.
      if (self.isValidShortUrl()) {
        self.shortUrl = self.replaceProtocol(self.shortUrl, protocol);
      }
    },
    hasProtocolChanged() {
      return self.qrProtocol !== self.originalProtocol;
    },
    setPreviewImageNull() {
      self.previewImage = ""
    },
    setBase64NonBrowserConvertedFile(str) {
      self.base64NonBrowserConvertedFile = str
    },
    isEps(format){
      return isEpsFormat(format)
    },

    fetchEpsAsBase64: flow(function* (epsUrl) {
      try {
        const manager = getEnv(self).mediaFileManager;
        const base64SrcImg = yield manager.convertEpsToBase64(epsUrl);
        self.setBase64NonBrowserConvertedFile(base64SrcImg);
      } catch (error) {
        console.error("Failed to fetch EPS as base64:", error);
      }
    }),
    updateName(title) {
      self.title = title;
    },
    selectQRConfig(obj) {
      if (obj && obj.qrConfig) {
        // Configuration selected
        self.setQRImageFormData(obj.qrConfig)
        const extend = JSON.parse(obj.qrConfig)
        // change download options
        self.handleDownload(extend)
        // change default download option to imageFormat
        self.imageFormat = self.qrDownloadStore.defaultImageFormat.value
        self.qrImageFormData = JSON.stringify({ ...extend, "imageFormat": self.imageFormat })
      } else {
        // Configuration cleared/removed - reset to default and regenerate image
        self.imageFormat = "png"
        self.qrImageFormData = JSON.stringify({ "imageFormat": self.imageFormat })
        self.generateImage()
      }
    },
    handleOpenEditModal(componentStore, component) {
      self.modalComponentStore = componentStore
      self.modalComponent = component;
      self.modalStore.toggle()
    },
    setQRImageFormData(data, updateOptions = false) {
      if(data) {
        const extend = JSON.parse(data)
        if (updateOptions) {
          self.handleDownload(extend)
        }
        self.qrImageFormData = JSON.stringify({ ...extend,"imageFormat": self.imageFormat})
        self.generateImage()
      }
    },
    setOnSelectChanges() {
      self.qrConfigSelectStore.setConsumer(self.selectQRConfig)
    },
    handleImgFormat(e) {
      self.imageFormat = e.value
      self.qrDownloadStore.handleDownloadImageFormat(e)
      const extend = self.qrImageFormData ? JSON.parse(self.qrImageFormData) : self.defaultImageConfig
      self.setQRImageFormData(JSON.stringify({...extend,"imageFormat": self.imageFormat}))
    },
    handleDownload(qrConfig) {
      // base designer template color mode, filter out png selection if cmyk
      //const colorMode = colorMode
      self.qrDownloadStore.handleDefaultImageFormat(qrConfig.colorMode, qrConfig.transparentBackground)

      self.qrDownloadStore.handleOptions(qrConfig, self.v1ImageConfig)
      self.imageFormat = self.qrDownloadStore.defaultImageFormat.value
      //self.qrDownloadStore.handleDownloadImageFormat(colorMode, transparentBackground)

    },
    copyUrl(){
      navigator.clipboard.writeText(self.shortUrl).then(() => {alert("'"+self.shortUrl+ "' was copied to your clipboard.")});
    },
    downloadConfig: flow(function* f(){
      const designerHttpClient = getEnv(self).httpClient; // Use authenticated httpClient from environment

      // Get code UUID from sibling state - handle both CodeListView (previewCodeAndEditDesignState) and EditCodeView (editCodeState)
      const rootUiStore = getRoot(self).rootUiStore;
      const codeUuid = self.isEditCodePage
        ? rootUiStore.editCodeState?.uuid
        : rootUiStore.previewCodeAndEditDesignState?.codeUuid;

      if (!codeUuid) {
        console.error("downloadConfig: codeUuid not available");
        return;
      }

      // Get image config UUID from selected configuration
      const imageConfigUuid = self.qrConfigSelectStore?.selectedConfig?.uuid;

      // Get image format from download store
      const imageFormat = self.qrDownloadStore.downloadImageFormat.value;

      // Use the metadata-rich variant: response body is a JSON object
      // containing base64Image plus rendering metadata. The legacy /images
      // endpoint returns just the data-URI string and is reserved for
      // external clients on the pre-Oct-2025 contract.
      const url = `/qr/codes/${codeUuid}/image-with-metadata`;
      const payload = {
        imageConfigUuid: imageConfigUuid || undefined,
        imageFormat: imageFormat
      };

      console.log("downloadConfig calling:", url, payload);

      let downloadImageBase64;
      let filename=self.title;

      try {
        const response = yield designerHttpClient.post(url, payload);

        if(response?.data?.name) {
          filename = response?.data?.name
          };
        // 2xx + base64Image is the only success contract.
        if (response?.data?.base64Image) {
          downloadImageBase64 = {
            base64Image: response.data.base64Image,
          };
        } else {
          // Defensive: server should not return 2xx without base64Image
          // under the /image-with-metadata contract, but if it does we
          // surface whatever error/message the body carries.
          const errorMsg = response?.data?.error || response?.data?.message || "Failed to download image";
          console.error("Failed to download image:", response);
          new Notification().setType("error").setMessage(errorMsg).send();
          return;
        }
      } catch (error) {
        // Axios throws on 4xx/5xx. The image controller returns 422 with the
        // upstream error payload when generation fails (e.g. invalid config,
        // upstream service rejection); read that off error.response.data so
        // we can show the specific diagnostic instead of a generic axios message.
        console.error("Error downloading image:", error);
        const upstreamMsg = error?.response?.data?.error || error?.response?.data?.message;
        const errorMsg = upstreamMsg || error?.message || "Failed to download image";
        new Notification().setType("error").setMessage(errorMsg).send();
        return;
      }

      if (!downloadImageBase64?.base64Image) {
        return;
      }

      console.log("downloadConfig downloadImageBase64:", downloadImageBase64);

      // Format filename
      if (filename === "") {
        filename = "qr_code";
      }
      let fileNameWithUnderScore = formatFileName(filename);

      // Determine file suffix and extension based on format
      let fileSuffix = "image";

      if(self.qrDownloadStore.downloadImageFormat.value === "png") {
        fileNameWithUnderScore = fileNameWithUnderScore + ".png";
      }
      if(self.qrDownloadStore.downloadImageFormat.value === "pdf"){
        fileSuffix = "application";
        fileNameWithUnderScore = fileNameWithUnderScore + ".pdf";
      }
      if(self.qrDownloadStore.downloadImageFormat.value === "svg"){
        self.imageFormat = "svg+xml";
        fileNameWithUnderScore = fileNameWithUnderScore + ".svg";
      }
      if(self.qrDownloadStore.downloadImageFormat.value === "eps") {
        fileNameWithUnderScore = fileNameWithUnderScore + ".eps";
      }
      if(self.qrDownloadStore.downloadImageFormat.value === "jpg") {
        fileNameWithUnderScore = fileNameWithUnderScore + ".jpeg";
      }
      if(self.qrDownloadStore.downloadImageFormat.value === "ai") {
        fileNameWithUnderScore = fileNameWithUnderScore + ".ai";
      }

      // Create data URL and download
      let downloadUrl = downloadImageBase64.base64Image;
      saveAs(downloadUrl, fileNameWithUnderScore);
    }),
    generateImage: flow(function* f() { // <= for now shortUrl but we need configParams = {}
      self.isLoading = true;
      const designerHttpClient = getEnv(self).httpClient; // Use authenticated httpClient from environment
      const previewManager = new PreviewManager(designerHttpClient, self.defaultImageConfig, self.v1ImageConfig);
      let epsPdfFormat;
      let fileSuffix = "image"
      const resp = yield previewManager.create(self, false)
      console.log("generateImage resp:", resp)
      self.isLoading = false
      if (resp?.status >= 200 && resp?.status < 300) {
        const scanabilityCheck = yield scannabilityCheck(resp.base64Image)
        self.handleValidQr(scanabilityCheck)
        self.previewImage = resp.base64Image
        self.moduleWidth = String(resp.moduleWidth)
        self.qrWidth = String(resp.qrWidth)
        self.qrWidthPixel = String(resp.qrWidthPixel)
        self.qrWidthInch = String(resp.qrWidthInch)
        self.qrWidthMM = String(resp.qrWidthMM)
        self.qrVersion = resp.qrVersion ? resp.qrVersion : 0
        self.modulePerSide = resp.modulePerSide
        self.errorCorrection = resp.errorCorrection || ""
        self.unit = resp.unit || ""
        self.quietZone = resp.quietZone || 0
        self.moduleWidthInPixel = String(resp.moduleWidthInPixel)
        self.moduleWidthInInch = String(resp.moduleWidthInInch)
        self.moduleWidthInMM = String(resp.moduleWidthInMM)
        self.bwr = resp.bwr != null ? String(resp.bwr) : null
      }
    }),
    fetchQRConfigs: flow(function* f(sortParams = {}){
      self.isLoading = true

      try {
        const queryParams = {
          page: 1,
          perPage: 50,
          ...sortParams,

        }
        const imageConfigManager = getEnv(self).imageConfigManager
        const response = yield imageConfigManager.fetchAll(queryParams);
        if(response !=null) {
          const qrConfigStore = response.results.map((el) => {
            return {
              "uuid": el.uuid,
              "name": el.name,
              "label": el.name,
              "image": el.qrPreview,
              "qrConfig": el.data
            }
          })
          self.qrConfigSelectStore.configStore.setConfigStore(qrConfigStore)
          self.isLoading = false;
        } else {
          self.isLoading = false;
        }
      } catch (error) {
        console.error('Failed to fetch all', error)
      }
    }),
    handleValidQr(validQr){
      self.validQr = validQr
    }
  })).views((self) => ({
    get bwrDisplay() {
      if (self.bwr == null || parseFloat(self.bwr) === 0) return null
      const formData = self.qrImageFormData ? JSON.parse(self.qrImageFormData) : {}
      if (formData.unitsOfMeasure === 'mm') {
        return { value: parseFloat((parseFloat(self.bwr) * 10).toFixed(4)), unit: formData.unitsOfMeasure }
      } else if (formData.unitsOfMeasure === 'cm') {
        return { value: parseFloat(self.bwr), unit: formData.unitsOfMeasure }
      } else {
        return { value: parseFloat(self.bwr), unit: formData.unitsOfMeasure || 'in'}
      }
    },
    shouldRenderBase64NonBrowserConvertedFile(){
      return self.base64NonBrowserConvertedFile !== "" && self.base64NonBrowserConvertedFile !== undefined
    },
    getMediaFileImageSrc(originalUrl, fileFormat) {
      // Check if this is an EPS format and trigger conversion if needed
      if (fileFormat && isEpsFormat(fileFormat)) {
        self.fetchEpsAsBase64(originalUrl);
      }else{
        self.setBase64NonBrowserConvertedFile("");
      }

      if (self.shouldRenderBase64NonBrowserConvertedFile()) {
        return self.base64NonBrowserConvertedFile;
      }

      if (originalUrl) {
        return getCacheBustedUrl(originalUrl);
      }

      return null;
    }
  }))


export default QrPreviewState;


